"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { CIM_STATUS_KEYS, CIM_STATUS_CLASS, CIM_WAYBILL_TYPE_KEYS } from '../../lib/status'

type WaybillRow = {
  id: string
  waybillNumber: string
  waybillType: string
  status: string
  consignorName: string
  consigneeName: string
  carrierName: string
  grossWeightKg: number | null
  createdAt: string
}

type WaybillsResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

function mapWaybillRow(item: Record<string, unknown>): WaybillRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    waybillNumber: typeof item.waybillNumber === 'string' ? item.waybillNumber : (typeof item.waybill_number === 'string' ? item.waybill_number : ''),
    waybillType: typeof item.waybillType === 'string' ? item.waybillType : (typeof item.waybill_type === 'string' ? item.waybill_type : 'CIM'),
    status: typeof item.status === 'string' ? item.status : 'DRAFT',
    consignorName: typeof item.consignorName === 'string' ? item.consignorName : (typeof item.consignor_name === 'string' ? item.consignor_name : ''),
    consigneeName: typeof item.consigneeName === 'string' ? item.consigneeName : (typeof item.consignee_name === 'string' ? item.consignee_name : ''),
    carrierName: typeof item.carrierName === 'string' ? item.carrierName : (typeof item.carrier_name === 'string' ? item.carrier_name : ''),
    grossWeightKg: typeof item.grossWeightKg === 'number' ? item.grossWeightKg : (typeof item.gross_weight_kg === 'number' ? item.gross_weight_kg : null),
    createdAt: typeof item.createdAt === 'string' ? item.createdAt : (typeof item.created_at === 'string' ? item.created_at : ''),
  }
}


export default function CimWaybillsPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'cim-waybills-list',
  })
  const [rows, setRows] = React.useState<WaybillRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const router = useRouter()

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'status',
      label: t('cim.waybills.filters.status', 'Status'),
      type: 'select' as const,
      options: CIM_STATUS_KEYS.map((s) => ({ value: s, label: t(`cim.waybills.status.${s}`, s) })),
    },
    {
      id: 'waybillType',
      label: t('cim.waybills.filters.waybillType', 'Type'),
      type: 'select' as const,
      options: CIM_WAYBILL_TYPE_KEYS.map((k) => ({ value: k, label: t(`cim.waybills.types.${k}`, k) })),
    },
  ], [t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const status = filterValues.status
    if (typeof status === 'string' && status.trim()) params.set('status', status)
    const waybillType = filterValues.waybillType
    if (typeof waybillType === 'string' && waybillType.trim()) params.set('waybillType', waybillType)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: WaybillsResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<WaybillsResponse>(
          `/api/cim/waybills?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('cim.waybills.error.load', 'Failed to load CIM waybills')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapWaybillRow).filter((row): row is WaybillRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('cim.waybills.error.load', 'Failed to load CIM waybills')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const handleDelete = React.useCallback(async (waybill: WaybillRow) => {
    if (!waybill.id) return
    const confirmed = await confirm({
      title: t('cim.waybills.deleteConfirm', 'Delete waybill {{waybillNumber}}?', { waybillNumber: waybill.waybillNumber }),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/cim/waybills?id=${encodeURIComponent(waybill.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('cim.waybills.error.delete', 'Failed to delete waybill') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((row) => row.id !== waybill.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('cim.waybills.flash.deleted', 'CIM waybill deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('cim.waybills.error.delete', 'Failed to delete waybill')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const columns = React.useMemo<ColumnDef<WaybillRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'waybillNumber',
        header: t('cim.waybills.columns.waybillNumber', 'Waybill No.'),
        cell: ({ row }) => <span className="font-mono font-medium">{row.original.waybillNumber}</span>,
      },
      {
        accessorKey: 'waybillType',
        header: t('cim.waybills.columns.waybillType', 'Type'),
        cell: ({ row }) => (
          <span className="text-sm">{t(`cim.waybills.types.${row.original.waybillType}`, row.original.waybillType)}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: t('cim.waybills.columns.status', 'Status'),
        cell: ({ row }) => {
          const statusClass = CIM_STATUS_CLASS[row.original.status] ?? ''
          return (
            <span className={statusClass}>
              {t(`cim.waybills.status.${row.original.status}`, row.original.status)}
            </span>
          )
        },
      },
      {
        accessorKey: 'consignorName',
        header: t('cim.waybills.columns.consignorName', 'Consignor'),
        cell: ({ row }) => row.original.consignorName || noValue,
      },
      {
        accessorKey: 'consigneeName',
        header: t('cim.waybills.columns.consigneeName', 'Consignee'),
        cell: ({ row }) => row.original.consigneeName || noValue,
      },
      {
        accessorKey: 'carrierName',
        header: t('cim.waybills.columns.carrierName', 'Carrier'),
        cell: ({ row }) => row.original.carrierName || noValue,
      },
      {
        accessorKey: 'grossWeightKg',
        header: t('cim.waybills.columns.grossWeightKg', 'Weight (kg)'),
        cell: ({ row }) => row.original.grossWeightKg !== null ? `${row.original.grossWeightKg}` : noValue,
      },
      {
        accessorKey: 'createdAt',
        header: t('cim.waybills.columns.createdAt', 'Created'),
        cell: ({ row }) => row.original.createdAt
          ? new Date(row.original.createdAt).toLocaleDateString()
          : noValue,
      },
    ]
  }, [t])

  return (
    <Page>
      <PageBody>
        <DataTable<WaybillRow>
          title={t('cim.waybills.title', 'CIM Waybills')}
          refreshButton={{
            label: t('cim.waybills.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); handleRefresh() },
          }}
          actions={(
            <Button asChild>
              <Link href="/backend/cim-waybills/create">
                {t('cim.waybills.actions.create', 'New Waybill')}
              </Link>
            </Button>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('cim.waybills.searchPlaceholder', 'Search by waybill number, consignor, or goods...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          perspective={{ tableId: 'cim.waybills.list' }}
          onRowClick={(row) => router.push(`/backend/cim-waybills/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('cim.waybills.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
